// src/components/admin/page-management/section-forms/SlideshowForm.js
import React, { useState } from 'react';
import ImageUploader from '../ImageUploader';

const SlideshowForm = ({ content, updateContent, pageSlug }) => {
  // Initialize slides array if it doesn't exist
  const slides = content?.slides || [
    { imageUrl: '', imageAlt: '', title: '', subtitle: '', text: '' }
  ];

  // State to track which slide is being edited
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Handler for slide image upload
  const handleImageUpload = (imagePath) => {
    const updatedSlides = [...slides];
    updatedSlides[currentSlideIndex] = {
      ...updatedSlides[currentSlideIndex],
      imageUrl: imagePath
    };
    updateContent('slides', updatedSlides);
  };

  // Add a new slide
  const addSlide = () => {
    const updatedSlides = [...slides, { imageUrl: '', imageAlt: '', title: '', subtitle: '', text: '' }];
    updateContent('slides', updatedSlides);
    setCurrentSlideIndex(updatedSlides.length - 1); // Select the new slide
  };

  // Remove the current slide
  const removeSlide = (index) => {
    if (slides.length <= 1) {
      alert('You must have at least one slide.');
      return;
    }
    
    const updatedSlides = slides.filter((_, i) => i !== index);
    updateContent('slides', updatedSlides);
    setCurrentSlideIndex(Math.min(currentSlideIndex, updatedSlides.length - 1));
  };

  // Update specific slide field
  const updateSlideField = (field, value) => {
    const updatedSlides = [...slides];
    updatedSlides[currentSlideIndex] = {
      ...updatedSlides[currentSlideIndex],
      [field]: value
    };
    updateContent('slides', updatedSlides);
  };

  return (
    <div>
      <h3 className="font-bold text-lg mb-4">Slideshow Section</h3>
      {/* Slide Management */}
<div className="mb-8 border-b border-gray-200 pb-6">
  <div className="flex justify-between items-center mb-4">
    <h4 className="text-lg font-semibold text-gray-800">Manage Slides</h4>
    <button
      type="button"
      onClick={addSlide}
      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md text-sm transition"
    >
      + Add Slide
    </button>
  </div>

  {/* Slide tabs */}
  <div className="flex overflow-x-auto mb-4 border-b border-gray-300">
    {slides.map((_, index) => (
      <button
        key={index}
        onClick={() => setCurrentSlideIndex(index)}
        className={`px-4 py-2 transition-all duration-150 whitespace-nowrap
          ${
            currentSlideIndex === index
              ? 'border-b-2 border-blue-500 text-blue-600 font-semibold'
              : 'text-gray-600 hover:text-blue-500'
          }`}
      >
        Slide {index + 1}
      </button>
    ))}
  </div>

  {/* Current slide edit form */}
  <div className="bg-white border rounded-lg p-6 shadow-sm">
    <div className="grid md:grid-cols-2 gap-6 mb-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
        <input
          type="text"
          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={slides[currentSlideIndex]?.imageUrl || ''}
          onChange={(e) => updateSlideField('imageUrl', e.target.value)}
        />
        <ImageUploader onImageUpload={handleImageUpload} pageSlug={pageSlug} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Image Alt Text</label>
        <input
          type="text"
          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={slides[currentSlideIndex]?.imageAlt || ''}
          onChange={(e) => updateSlideField('imageAlt', e.target.value)}
          placeholder="Descriptive text for accessibility"
        />
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-6 mb-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
        <input
          type="text"
          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={slides[currentSlideIndex]?.subtitle || ''}
          onChange={(e) => updateSlideField('subtitle', e.target.value)}
          placeholder="e.g., SINCE 1998"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input
          type="text"
          className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={slides[currentSlideIndex]?.title || ''}
          onChange={(e) => updateSlideField('title', e.target.value)}
          placeholder="Main heading text"
        />
      </div>
    </div>

    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-1">Text</label>
      <textarea
        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
        rows="4"
        value={slides[currentSlideIndex]?.text || ''}
        onChange={(e) => updateSlideField('text', e.target.value)}
        placeholder="Supporting text description"
      />
    </div>

    <div className="text-right">
      <button
        type="button"
        onClick={() => removeSlide(currentSlideIndex)}
        className={`bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md text-sm transition ${
          slides.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        disabled={slides.length <= 1}
      >
        Remove This Slide
      </button>
    </div>
  </div>
</div>

      {/* Global Slideshow Settings */}
<div className="space-y-6">
  <h4 className="text-lg font-semibold text-gray-800 mb-2">Slideshow Settings</h4>

  <div className="bg-white border rounded-lg p-5 shadow-sm">
    <label className="block text-sm font-medium text-gray-700 mb-1">Height</label>
    <input
      type="text"
      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
      value={content?.height || '800px'}
      onChange={(e) => updateContent('height', e.target.value)}
      placeholder="e.g., 800px, 90vh"
    />
    <p className="text-xs text-gray-500 mt-1">Height of the slideshow (CSS value)</p>
  </div>

  <div className="bg-white border rounded-lg p-5 shadow-sm">
    <label className="block text-sm font-medium text-gray-700 mb-2">Overlay Opacity</label>
    <input
      type="range"
      min="0"
      max="1"
      step="0.1"
      className="w-full accent-indigo-500"
      value={content?.overlayOpacity || '0.6'}
      onChange={(e) => updateContent('overlayOpacity', e.target.value)}
    />
    <div className="flex justify-between text-xs text-gray-500 mt-1">
      <span>Transparent</span>
      <span>Current: {content?.overlayOpacity || '0.6'}</span>
      <span>Opaque</span>
    </div>
  </div>

  <div className="grid md:grid-cols-2 gap-6">
    <div className="bg-white border rounded-lg p-5 shadow-sm">
      <label className="block text-sm font-medium text-gray-700 mb-1">Text Alignment</label>
      <select
        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        value={content?.textAlignment || 'center'}
        onChange={(e) => updateContent('textAlignment', e.target.value)}
      >
        <option value="left">Left</option>
        <option value="center">Center</option>
        <option value="right">Right</option>
      </select>
    </div>

    <div className="bg-white border rounded-lg p-5 shadow-sm">
      <label className="block text-sm font-medium text-gray-700 mb-1">Transition Speed (ms)</label>
      <input
        type="number"
        min="1000"
        step="500"
        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        value={content?.transitionSpeed || 5000}
        onChange={(e) => updateContent('transitionSpeed', parseInt(e.target.value))}
      />
      <p className="text-xs text-gray-500 mt-1">Time between slide transitions in milliseconds (1000ms = 1 second)</p>
    </div>
  </div>

  <div className="grid md:grid-cols-2 gap-6">
    <div className="bg-white border rounded-lg p-5 shadow-sm">
      <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
      <input
        type="text"
        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        value={content?.buttonText || ''}
        onChange={(e) => updateContent('buttonText', e.target.value)}
        placeholder="e.g., Learn More"
      />
    </div>

    <div className="bg-white border rounded-lg p-5 shadow-sm">
      <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
      <input
        type="text"
        className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        value={content?.buttonLink || ''}
        onChange={(e) => updateContent('buttonLink', e.target.value)}
        placeholder="e.g., #learn-more or /contact"
      />
    </div>
  </div>

  <div className="bg-white border rounded-lg p-5 shadow-sm">
    <label className="block text-sm font-medium text-gray-700 mb-1">Button Style</label>
    <select
      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
      value={content?.buttonStyle || 'primary'}
      onChange={(e) => updateContent('buttonStyle', e.target.value)}
    >
      <option value="primary">Primary (White)</option>
      <option value="secondary">Secondary (Amber)</option>
      <option value="outline">Outline</option>
      <option value="link">Link</option>
    </select>
  </div>
</div>

{/* Live Preview */}
<div className="mt-8 border-t pt-6">
  <h4 className="text-lg font-semibold text-gray-800 mb-4">Live Preview</h4>

  <div
    className="relative w-full flex items-center justify-center rounded-lg overflow-hidden border shadow-md transition-all duration-300"
    style={{
      height: content?.height || '800px',
      background: `linear-gradient(rgba(0,0,0,${content?.overlayOpacity || 0.6}), rgba(0,0,0,${content?.overlayOpacity || 0.6})), url('${slides?.[0]?.imageUrl || 'https://via.placeholder.com/1600x800'}') center/cover no-repeat`,
      textAlign: content?.textAlignment || 'center',
    }}
  >
    <div className="p-6 max-w-2xl text-white z-10">
      {slides?.[0]?.subtitle && (
        <p className="text-sm uppercase tracking-widest mb-1">{slides[0].subtitle}</p>
      )}
      {slides?.[0]?.title && (
        <h2 className="text-3xl font-bold mb-2">{slides[0].title}</h2>
      )}
      {slides?.[0]?.text && (
        <p className="text-base opacity-90 mb-4">{slides[0].text}</p>
      )}
      {content?.buttonText && (
        <a
          href={content?.buttonLink || '#'}
          className={`inline-block mt-2 px-5 py-2 rounded-md font-medium transition 
            ${
              content.buttonStyle === 'primary'
                ? 'bg-white text-gray-900 hover:bg-gray-100'
                : content.buttonStyle === 'secondary'
                ? 'bg-amber-400 text-black hover:bg-amber-300'
                : content.buttonStyle === 'outline'
                ? 'border border-white text-white hover:bg-white hover:text-black'
                : 'text-white underline hover:text-amber-200'
            }`}
        >
          {content.buttonText}
        </a>
      )}
    </div>

    {/* Optional dark overlay fallback if image not loaded */}
    <div className="absolute inset-0 bg-black opacity-30 z-0 pointer-events-none"></div>
  </div>
</div>

     
    </div>
  );
};

export default SlideshowForm;